import type { Result } from 'neverthrow';

import { apiRequest, type ApiError } from './client';

/**
 * Frontend mirror of `packages/db/src/orders.ts:OrderSchema`. Kept as a
 * manual interface so the SPA stays dependency-free of the backend
 * packages — the wire format is the contract.
 */
export interface OrderAddress {
  readonly name?: string;
  readonly company?: string;
  readonly line1?: string;
  readonly line2?: string;
  readonly city?: string;
  readonly state?: string;
  readonly postal_code?: string;
  readonly country?: string;
}

export interface OrderLineItem {
  readonly sku?: string;
  readonly name: string;
  readonly quantity: number;
}

export interface Order {
  readonly order_id: string;
  readonly tenant_id: string;
  readonly connection_id: string;
  readonly source: 'bigcommerce';
  readonly bc_order_id: number;
  readonly bc_status_id: number;
  readonly bc_status: string;
  readonly bc_date_created: string;
  readonly bc_date_modified: string;
  readonly currency: string;
  readonly total: string;
  readonly customer_name?: string;
  readonly customer_email?: string;
  readonly items_count?: number;
  readonly ship_to?: OrderAddress;
  /**
   * The shipping method the customer chose at BC checkout (e.g. "Royal
   * Mail Tracked 48", "Free Shipping"). Populated by the sync core
   * from `shipping_addresses[0].shipping_method` per ADR 0020 Slice A.
   */
  readonly customer_shipping_method?: string;
  readonly line_items?: readonly OrderLineItem[];
  readonly raw?: unknown;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface ListOrdersResponse {
  readonly orders: readonly Order[];
}

export function listOrders(): Promise<Result<ListOrdersResponse, ApiError>> {
  return apiRequest<ListOrdersResponse>({ method: 'GET', path: '/orders' });
}

export function getOrder(orderId: string): Promise<Result<Order, ApiError>> {
  return apiRequest<Order>({
    method: 'GET',
    path: `/orders/${encodeURIComponent(orderId)}`,
  });
}

export const ORDERS_QUERY_KEY = ['orders'] as const;
export const ORDER_QUERY_KEY = (orderId: string) => ['order', orderId] as const;
